import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { Grid } from '@ui-propulsion/react-components';
import parse from 'html-react-parser';
import CtaButton from '../../ctaButton/ctaButton';
import handleCTAClick from '../../../services/helpers/handleCTAClick';
import SmartImage from '../../smartImage/smartImage';
import IconList from '../../iconList/iconList'; // Import IconList
import styles from './twoColumnTextImageDesktop.module.scss';

export default function TwoColumnTextImageDesktop({
  title = '',
  subHeader = '',
  description = '',
  list = [],
  images = [],
  cta = null,
  textBoxPosition = '',
  ctaType = '',
  imageDirection = 'horizontal',
  theme = 'white',
  imageColumnSize = '2',
  imagePosition = 'center',
  imageVerticalAlignment = 'middle',
  roundedImage = '',
}) {
  const [gridSizeImageColumn, setGridSizeImageColumn] = useState({});
  const [gridSizeTextColumn, setGridSizeTextColumn] = useState({});
  useEffect(() => {
    const defaultGridSize = { sm: 12, md: 12, lg: 6 }; // 100% on tablet (md)

    if (imageColumnSize === '1') {
      setGridSizeImageColumn({ sm: 12, md: 6, lg: 4 });
      setGridSizeTextColumn({ sm: 12, md: 6, lg: 8 });
    } else if (imageColumnSize === '2') {
      setGridSizeImageColumn({ sm: 12, md: 6, lg: 6 });
      setGridSizeTextColumn({ sm: 12, md: 6, lg: 6 });
    } else if (imageColumnSize === '3') {
      setGridSizeImageColumn({ sm: 12, md: 6, lg: 8 });
      setGridSizeTextColumn({ sm: 12, md: 6, lg: 4 });
    } else {
      setGridSizeImageColumn(defaultGridSize);
      setGridSizeTextColumn(defaultGridSize);
    }
  }, [imageColumnSize]);
  const imageClass = `${styles.image} ${styles[imageDirection]} ${
    roundedImage.length > 0 ? styles[roundedImage] : ''
  }`;
  const imageHorizontalAlignClass = `${styles[imagePosition]}`;
  const imageVerticalAlignClass = `${styles[imageVerticalAlignment]}`;
  const imageSectionClass = `${imageHorizontalAlignClass} ${imageVerticalAlignClass}`;
  const contentPaddingClass = `${styles.content} ${
    textBoxPosition === 'left'
      ? styles.contentPaddingRight
      : styles.contentPaddingLeft
  } `;
  const ctaButton = cta && (
    <div className={`${styles.ctaContainer} ${styles[ctaType]}`}>
      <CtaButton
        aria-label="radius-bank"
        data-testid="cta"
        label={cta?.label}
        variant={cta?.variant}
        color={cta?.color}
        type="button"
        fullWidth
        onClick={(e) => handleCTAClick(e, cta)}
        href={cta?.link}
        id="radius_module_cta"
      />
    </div>
  );
  const textSection = (
    <Grid
      item
      container
      {...gridSizeTextColumn}
      className={contentPaddingClass}
    >
      {subHeader && <div className={styles.subHeader}>{parse(subHeader)}</div>}
      {title && <h2 className={styles.title}>{parse(title)}</h2>}
      {description && (
        <div className={styles.description}>{parse(description)}</div>
      )}
      {list?.length > 0 && <IconList list={list} />}
      {/* Render IconList if list has items */}
      {cta && <div className={styles.desktopLayout}>{ctaButton}</div>}
    </Grid>
  );
  const imageSection = (
    <Grid
      item
      container
      data-testid="image-section"
      className={imageSectionClass}
      {...gridSizeImageColumn}
    >
      <div className={imageClass}>
        {images?.map((img, ci) =>
          img?.link ? (
            <Link href={img?.link} key={`row-${ci}`.toString()}>
              <SmartImage
                src={img?.src}
                alt={img?.alt}
                loading="lazy"
                width={img.width}
                height={img.height}
              />
            </Link>
          ) : (
            <SmartImage
              src={img?.src}
              alt={img?.alt}
              loading="lazy"
              key={`row-${ci}`.toString()}
              width={img.width}
              height={img.height}
            />
          )
        )}
      </div>
    </Grid>
  );

  return (
    <section>
      <div className={`${styles.info} ${styles[theme]}`}>
        <Grid container className={styles.twoColumnTextImageDesktopGrid}>
          {textBoxPosition === 'left' ? textSection : imageSection}
          {textBoxPosition === 'left' ? imageSection : textSection}
        </Grid>
      </div>
    </section>
  );
}

TwoColumnTextImageDesktop.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      alt: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  textBoxPosition: PropTypes.string,
  ctaType: PropTypes.string,
  imageDirection: PropTypes.oneOf(['vertical', 'horizontal']),
  theme: PropTypes.string,
  imageColumnSize: PropTypes.string,
  imagePosition: PropTypes.string,
  imageVerticalAlignment: PropTypes.string,
};
